import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCustomers } from '../hooks/useCustomers';
import { useWorksheets } from '../hooks/useWorksheets';
import { Worksheet, CreateWorksheetData } from '../types/worksheet';
import { Customer } from '../types/customer';
import SearchableSelect from '../components/common/SearchableSelect';
import DateTimePicker from '../components/common/DateTimePicker';
import DigitalSignature from '../components/common/DigitalSignature';
import WorksheetPhotos from '../components/worksheets/WorksheetPhotos';
import CreateInvoiceFromSource, { toInvoiceItems } from '../components/billing/CreateInvoiceFromSource';
import { generateWorksheetPdf, loadWorksheetPhotosForPdf } from '../lib/worksheetPdfGenerator';
import {
  BoltIcon, CheckCircleIcon, UserPlusIcon, UserIcon, WrenchScrewdriverIcon, CameraIcon, PencilSquareIcon,
  DocumentArrowDownIcon, BanknotesIcon, LinkIcon, PlusIcon, MinusIcon, ArrowLeftIcon, ClipboardDocumentListIcon, ShareIcon
} from '@heroicons/react/24/outline';

type WorkType = CreateWorksheetData['workType'];
const WORK_TYPES: { value: WorkType; label: string; emoji: string }[] = [
  { value: 'maintenance', label: 'Karbantartás', emoji: '🔧' },
  { value: 'repair', label: 'Javítás', emoji: '🛠️' },
  { value: 'installation', label: 'Telepítés', emoji: '📦' },
  { value: 'inspection', label: 'Ellenőrzés', emoji: '🔍' },
  { value: 'other', label: 'Egyéb', emoji: '📝' }
];

const customerLabel = (c: Customer) => c.type === 'organization' ? (c.companyName || 'Cég') : [c.lastName, c.firstName].filter(Boolean).join(' ') || c.email || 'Ügyfél';
const customerAddress = (c?: Customer) => c?.address ? [c.address.postalCode, c.address.city, c.address.street].filter(Boolean).join(' ') : '';

/**
 * Gyors munkalap – telefonra. Egy képernyő: ügyfél → munka → idő → mentés,
 * utána fotó, aláírás, lezárás, PDF / számla / link az ügyfélnek.
 */
const QuickWorksheet: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { customers, createCustomer } = useCustomers();
  const { worksheets, addWorksheet, updateWorksheet, addSignature, changeStatus, generatePublicLink } = useWorksheets();

  // ---- 1. lépés: űrlap
  const [customerId, setCustomerId] = useState(params.get('customer') || '');
  const [newCustomer, setNewCustomer] = useState(false);
  const [nc, setNc] = useState({ name: '', phone: '', email: '', city: '', street: '' });
  const [workType, setWorkType] = useState<WorkType>('maintenance');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [materials, setMaterials] = useState('');
  const [showMaterials, setShowMaterials] = useState(false);
  const [hours, setHours] = useState(1);
  const [km, setKm] = useState<number | ''>('');
  const [date, setDate] = useState<Date>(new Date());
  const [saving, setSaving] = useState(false);

  // ---- 2. lépés: mentett munkalap
  const [worksheetId, setWorksheetId] = useState<string | null>(params.get('id'));
  const worksheet = useMemo(() => worksheets.find(w => w.id === worksheetId) || null, [worksheets, worksheetId]);
  const [sigOpen, setSigOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const selectedCustomer = customers.find(c => c.id === customerId);
  const wtMeta = WORK_TYPES.find(w => w.value === workType)!;
  const autoTitle = `${wtMeta.label}${selectedCustomer ? ` – ${customerLabel(selectedCustomer)}` : newCustomer && nc.name ? ` – ${nc.name}` : ''}`;
  useEffect(() => { /* a címet nem írjuk felül, ha a szerelő már beleírt */ }, []);

  const customerOptions = useMemo(() => customers.filter(c => c.status !== 'inactive').map(c => ({ value: c.id, label: `${customerLabel(c)}${c.phone ? ` · ${c.phone}` : ''}` })), [customers]);

  const canSave = (newCustomer ? nc.name.trim().length > 1 : !!customerId) && (details.trim().length > 0 || title.trim().length > 0) && !saving;

  const save = async () => {
    if (!canSave || !user) return;
    setSaving(true);
    try {
      let cid = customerId;
      let cust = selectedCustomer;
      if (newCustomer) {
        const parts = nc.name.trim().split(/\s+/);
        const isOrg = /kft|zrt|bt\.?|nyrt|kkt|ev\.?|e\.v\.|egyesület|alapítvány/i.test(nc.name);
        const data: any = {
          type: isOrg ? 'organization' : 'individual',
          companyName: isOrg ? nc.name.trim() : '',
          lastName: isOrg ? '' : parts[0] || '',
          firstName: isOrg ? '' : parts.slice(1).join(' '),
          email: nc.email.trim(),
          phone: nc.phone.trim(),
          address: { street: nc.street.trim(), city: nc.city.trim(), postalCode: '', country: 'Magyarország' },
          contactPerson: { name: nc.name.trim(), email: nc.email.trim(), phone: nc.phone.trim(), position: '' },
          organizations: user.organizationId ? [user.organizationId] : [],
          documents: [], isActive: true, notes: 'Gyors munkalapból létrehozva'
        };
        const id = await createCustomer(data);
        if (!id) throw new Error('Az ügyfél létrehozása nem sikerült');
        cid = id;
        cust = undefined;
      }
      const finalTitle = title.trim() || autoTitle;
      const ws: CreateWorksheetData = {
        title: finalTitle,
        description: '',
        customerId: cid,
        status: 'in_progress',
        priority: 'medium',
        assignedTo: user.id,
        dueDate: date,
        worksheetDate: date,
        deploymentOccurred: true,
        deploymentDate: date,
        workType,
        workDetails: details.trim(),
        materials: materials.trim() || undefined,
        actualHours: hours,
        travelKmTotal: km === '' ? undefined : Number(km),
        location: cust ? customerAddress(cust) : [nc.city, nc.street].filter(Boolean).join(' ') || undefined,
        mode: 'field',
        organizationId: user.organizationId,
        items: [], tasks: [], notes_list: []
      };
      const id = await addWorksheet(ws);
      setWorksheetId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Munkalap mentve – jöhet a fotó és az aláírás');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'A mentés nem sikerült');
    } finally { setSaving(false); }
  };

  const sign = async (signatureData: string, signerName: string, signerRole: string) => {
    if (!worksheet) return;
    try {
      let location: string | undefined;
      if (navigator.geolocation) {
        try { const p = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 })); location = `${p.coords.latitude.toFixed(6)}, ${p.coords.longitude.toFixed(6)}`; } catch { /* nincs helyadat */ }
      }
      await addSignature(worksheet.id, { signedBy: user?.id || 'field', signerName, signerRole, signatureData, signedAt: new Date(), location, deviceInfo: navigator.userAgent.slice(0, 120) });
      setSigOpen(false);
      toast.success('Aláírás rögzítve');
    } catch (e) { toast.error('Az aláírás mentése nem sikerült'); }
  };

  const finish = async () => {
    if (!worksheet) return;
    setBusy('finish');
    try { await changeStatus(worksheet.id, 'completed', 'Gyors munkalap lezárva'); toast.success('Munkalap lezárva'); }
    catch { toast.error('A lezárás nem sikerült'); }
    finally { setBusy(null); }
  };

  const pdf = async () => {
    if (!worksheet) return;
    setBusy('pdf');
    try { const photos = await loadWorksheetPhotosForPdf(worksheet); generateWorksheetPdf({ worksheet, timeEntries: [], customerName: selectedCustomer ? customerLabel(selectedCustomer) : (customers.find(c => c.id === worksheet.customerId) ? customerLabel(customers.find(c => c.id === worksheet.customerId)!) : ''), t, photos }); }
    catch { toast.error('A PDF nem készült el'); }
    finally { setBusy(null); }
  };

  const share = async () => {
    if (!worksheet) return;
    setBusy('link');
    try {
      const link = worksheet.publicLink || await generatePublicLink(worksheet.id);
      const text = `Munkalap: ${worksheet.worksheetNumber} – ${worksheet.title}`;
      if ((navigator as any).share) { try { await (navigator as any).share({ title: text, text, url: link }); return; } catch { /* megszakítva */ } }
      await navigator.clipboard.writeText(link);
      toast.success('Link vágólapra másolva');
    } catch { toast.error('A link nem készült el'); }
    finally { setBusy(null); }
  };

  const reset = () => {
    setWorksheetId(null); setCustomerId(''); setNewCustomer(false); setNc({ name: '', phone: '', email: '', city: '', street: '' });
    setWorkType('maintenance'); setTitle(''); setDetails(''); setMaterials(''); setShowMaterials(false); setHours(1); setKm(''); setDate(new Date());
    navigate('/worksheets/quick', { replace: true });
    window.scrollTo({ top: 0 });
  };

  const wsCustomer = worksheet ? customers.find(c => c.id === worksheet.customerId) : undefined;
  const invoiceSource = worksheet ? {
    type: 'worksheet' as const, id: worksheet.id, number: worksheet.worksheetNumber, customerId: worksheet.customerId,
    items: (worksheet.items && worksheet.items.length) ? toInvoiceItems(worksheet.items) : toInvoiceItems([{ name: `Munkadíj – ${worksheet.title}`, quantity: worksheet.actualHours || 1, unit: 'óra', unitPrice: 0, taxRate: 27 }]),
    notes: `Munkalap: ${worksheet.worksheetNumber}${worksheet.workDetails ? `\n${worksheet.workDetails}` : ''}`
  } : null;

  const Section: React.FC<{ n: number; title: string; icon: React.ElementType; done?: boolean; children: React.ReactNode }> = ({ n, title: st, icon: Icon, done, children }) => (
    <section className="crm-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)]">
        <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-semibold ${done ? 'bg-emerald-500 text-white' : 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'}`}>{done ? '✓' : n}</span>
        <Icon className="w-4 h-4 text-gray-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">{st}</h2>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-lg mx-auto px-3 sm:px-4 py-3 sm:py-6 space-y-3 pb-28">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/worksheets')} className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Vissza"><ArrowLeftIcon className="w-5 h-5" /></button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-1.5"><BoltIcon className="w-5 h-5 text-primary-600" />Gyors munkalap</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{worksheet ? `${worksheet.worksheetNumber} · ${worksheet.status === 'completed' ? 'lezárva' : 'folyamatban'}` : 'Ügyfél → munka → mentés → fotó, aláírás'}</p>
          </div>
        </div>

        {!worksheet ? (
          <>
            <Section n={1} title="Ügyfél" icon={UserIcon} done={newCustomer ? nc.name.trim().length > 1 : !!customerId}>
              {!newCustomer ? (
                <>
                  <SearchableSelect options={customerOptions} value={customerId} onChange={setCustomerId} placeholder="Keresés név vagy telefon szerint…" searchPlaceholder="Ügyfél neve…" />
                  {selectedCustomer && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3">
                      {selectedCustomer.phone && <a href={`tel:${selectedCustomer.phone}`} className="text-primary-600">{selectedCustomer.phone}</a>}
                      {customerAddress(selectedCustomer) && <span>{customerAddress(selectedCustomer)}</span>}
                    </div>
                  )}
                  <button type="button" onClick={() => { setNewCustomer(true); setCustomerId(''); }} className="crm-btn crm-btn-secondary w-full h-11"><UserPlusIcon className="w-4 h-4" />Új ügyfél a helyszínen</button>
                </>
              ) : (
                <>
                  <input value={nc.name} onChange={e => setNc({ ...nc, name: e.target.value })} placeholder="Név vagy cégnév *" autoFocus className="w-full border rounded-lg px-3 h-11" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={nc.phone} onChange={e => setNc({ ...nc, phone: e.target.value })} placeholder="Telefon" type="tel" className="w-full border rounded-lg px-3 h-11" />
                    <input value={nc.email} onChange={e => setNc({ ...nc, email: e.target.value })} placeholder="E-mail" type="email" className="w-full border rounded-lg px-3 h-11" />
                  </div>
                  <div className="grid grid-cols-[1fr_2fr] gap-2">
                    <input value={nc.city} onChange={e => setNc({ ...nc, city: e.target.value })} placeholder="Város" className="w-full border rounded-lg px-3 h-11" />
                    <input value={nc.street} onChange={e => setNc({ ...nc, street: e.target.value })} placeholder="Utca, házszám" className="w-full border rounded-lg px-3 h-11" />
                  </div>
                  <button type="button" onClick={() => setNewCustomer(false)} className="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white">← Meglévő ügyfél választása</button>
                </>
              )}
            </Section>

            <Section n={2} title="Munka" icon={WrenchScrewdriverIcon} done={details.trim().length > 0}>
              <div className="grid grid-cols-5 gap-1.5">
                {WORK_TYPES.map(w => (
                  <button key={w.value} type="button" onClick={() => setWorkType(w.value)} className={`h-14 rounded-lg border text-[11px] font-medium flex flex-col items-center justify-center gap-0.5 ${workType === w.value ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'border-[var(--crm-border)] text-gray-600 dark:text-gray-300'}`}>
                    <span className="text-lg leading-none">{w.emoji}</span>{w.label}
                  </button>
                ))}
              </div>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder={autoTitle} className="w-full border rounded-lg px-3 h-11 font-medium" />
              <textarea value={details} onChange={e => setDetails(e.target.value)} rows={4} placeholder="Mit csináltál? (hiba, elvégzett munka, megjegyzés az ügyfélnek)" className="w-full border rounded-lg px-3 py-2.5 text-base leading-relaxed" />
              {showMaterials ? (
                <textarea value={materials} onChange={e => setMaterials(e.target.value)} rows={2} placeholder="Felhasznált anyagok (pl. 2 db szűrő, 1 m cső)" className="w-full border rounded-lg px-3 py-2.5" />
              ) : (
                <button type="button" onClick={() => setShowMaterials(true)} className="text-xs text-primary-600">+ Felhasznált anyagok</button>
              )}
            </Section>

            <Section n={3} title="Idő és kiszállás" icon={ClipboardDocumentListIcon} done>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Munkaidő</div>
                  <div className="flex items-center rounded-lg border border-[var(--crm-border)] overflow-hidden h-11">
                    <button type="button" onClick={() => setHours(h => Math.max(0.5, Math.round((h - 0.5) * 2) / 2))} className="w-11 h-full inline-flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"><MinusIcon className="w-4 h-4" /></button>
                    <div className="flex-1 text-center font-semibold tabular-nums">{hours} óra</div>
                    <button type="button" onClick={() => setHours(h => Math.round((h + 0.5) * 2) / 2)} className="w-11 h-full inline-flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"><PlusIcon className="w-4 h-4" /></button>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Kiszállás km</div>
                  <input type="number" inputMode="decimal" min={0} value={km} onChange={e => setKm(e.target.value === '' ? '' : Number(e.target.value))} placeholder="–" className="w-full border rounded-lg px-3 h-11 tabular-nums" />
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Dátum</div>
                <DateTimePicker showTime={false} value={date} onChange={d => d && setDate(d)} />
              </div>
            </Section>

            <div className="fixed bottom-0 inset-x-0 p-3 bg-[var(--crm-surface)]/95 backdrop-blur border-t border-[var(--crm-border)] z-30">
              <div className="max-w-lg mx-auto">
                <button type="button" onClick={save} disabled={!canSave} className="crm-btn crm-btn-primary w-full h-12 text-base disabled:opacity-50">
                  {saving ? 'Mentés…' : <><CheckCircleIcon className="w-5 h-5" />Munkalap mentése</>}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="crm-card p-4">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{worksheet.title}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{wsCustomer ? customerLabel(wsCustomer) : ''}{worksheet.actualHours ? ` · ${worksheet.actualHours} óra` : ''}{worksheet.travelKmTotal ? ` · ${worksheet.travelKmTotal} km` : ''}</div>
              {worksheet.workDetails && <p className="text-sm text-gray-700 dark:text-gray-200 mt-2 whitespace-pre-wrap">{worksheet.workDetails}</p>}
              {worksheet.status !== 'completed' && <button type="button" onClick={() => navigate(`/worksheets?open=${worksheet.id}`)} className="text-xs text-primary-600 mt-2">Részletes szerkesztés →</button>}
            </div>

            <Section n={4} title="Fotók" icon={CameraIcon} done={(worksheet.photos || []).length > 0}>
              <WorksheetPhotos worksheet={worksheet as Worksheet} readOnly={worksheet.status === 'completed'} />
            </Section>

            <Section n={5} title="Aláírás" icon={PencilSquareIcon} done={(worksheet.signatures || []).length > 0}>
              {(worksheet.signatures || []).length > 0 ? (
                <div className="space-y-2">
                  {(worksheet.signatures || []).map(s => (
                    <div key={s.id} className="flex items-center gap-3 rounded-lg border border-[var(--crm-border)] p-2">
                      <img src={s.signatureData} alt="" className="h-10 w-24 object-contain bg-white rounded" />
                      <div className="text-xs"><div className="font-medium text-gray-900 dark:text-gray-100">{s.signerName}</div><div className="text-gray-500">{s.signerRole}</div></div>
                    </div>
                  ))}
                  {worksheet.status !== 'completed' && <button type="button" onClick={() => setSigOpen(true)} className="text-xs text-primary-600">+ Másik aláírás</button>}
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Add oda a telefont az ügyfélnek – ujjal aláírja. Ha nincs jelen, küldd el neki a linket, ott is aláírhatja.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setSigOpen(true)} className="crm-btn crm-btn-primary h-11"><PencilSquareIcon className="w-4 h-4" />Aláírás most</button>
                    <button type="button" onClick={share} disabled={busy === 'link'} className="crm-btn crm-btn-secondary h-11"><ShareIcon className="w-4 h-4" />Link küldése</button>
                  </div>
                </>
              )}
            </Section>

            <Section n={6} title={worksheet.status === 'completed' ? 'Kész' : 'Lezárás'} icon={CheckCircleIcon} done={worksheet.status === 'completed'}>
              {worksheet.status !== 'completed' ? (
                <button type="button" onClick={finish} disabled={busy === 'finish'} className="crm-btn crm-btn-primary w-full h-12 text-base"><CheckCircleIcon className="w-5 h-5" />{busy === 'finish' ? 'Lezárás…' : 'Munkalap lezárása'}</button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={pdf} disabled={busy === 'pdf'} className="crm-btn crm-btn-secondary h-11"><DocumentArrowDownIcon className="w-4 h-4" />PDF</button>
                  <button type="button" onClick={share} disabled={busy === 'link'} className="crm-btn crm-btn-secondary h-11"><LinkIcon className="w-4 h-4" />Link az ügyfélnek</button>
                  <button type="button" onClick={() => setInvoiceOpen(true)} className="crm-btn crm-btn-primary h-11 col-span-2"><BanknotesIcon className="w-4 h-4" />Számla készítése</button>
                </div>
              )}
              <button type="button" onClick={reset} className="w-full h-10 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">+ Új gyors munkalap</button>
            </Section>
          </>
        )}
      </div>

      <DigitalSignature isOpen={sigOpen} onClose={() => setSigOpen(false)} onSave={sign} title="Ügyfél aláírása" />
      {invoiceSource && <CreateInvoiceFromSource isOpen={invoiceOpen} onClose={() => setInvoiceOpen(false)} source={invoiceSource} />}
    </div>
  );
};

export default QuickWorksheet;
